import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import {
  selectProfileData,
  selectProfileError,
  selectProfileFullName,
  selectProfileLoading,
  selectProfileMobileNumber,
  selectProfileUpdating,
} from "../redux/selector";
import { actions } from "../redux/slice";
import {
  ProfileSelectFieldEnum,
  UpdateProfilePayload,
} from "../types";

export const PROFILE_SELECT_FIELDS = [
  {
    field: ProfileSelectFieldEnum.CALENDAR_WEEK_START,
    labelKey: "PROFILE.CALENDAR_WEEK_START",
    value: "sunday",
    options: [{ value: "sunday", labelKey: "PROFILE.OPTIONS.SUNDAY" }],
  },
  {
    field: ProfileSelectFieldEnum.TIME_FORMAT,
    labelKey: "PROFILE.TIME_FORMAT",
    value: "12h",
    options: [{ value: "12h", labelKey: "PROFILE.OPTIONS.12_HOURS" }],
  },
  {
    field: ProfileSelectFieldEnum.DATE_FORMAT,
    labelKey: "PROFILE.DATE_FORMAT",
    value: "dmy",
    options: [{ value: "dmy", labelKey: "PROFILE.OPTIONS.DAY_MONTH_YEAR" }],
  },
];

export const useProfile = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const profile = useSelector(selectProfileData);
  const loading = useSelector(selectProfileLoading);
  const updating = useSelector(selectProfileUpdating);
  const error = useSelector(selectProfileError);
  const fullName = useSelector(selectProfileFullName);
  const mobileNumber = useSelector(selectProfileMobileNumber);

  useEffect(() => {
    dispatch(actions.getProfile());
  }, [dispatch]);

  const avatarInitials = fullName
    .trim()
    .split(/\s+/)
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  const showSkeleton = loading || (!profile && !error);

  const setFullName = (value: string) => {
    dispatch(
      actions.setProfileFormField({ name: "fullName", value }),
    );
  };

  const setMobileNumber = (value: string) => {
    dispatch(
      actions.setProfileFormField({ name: "mobileNumber", value }),
    );
  };

  const updateProfile = (
    data: UpdateProfilePayload,
    callback?: () => void,
  ) => {
    if (!profile?.UserID) return;

    dispatch(
      actions.updateProfile({
        id: profile.UserID,
        data,
        callback,
      }),
    );
  };

  const handleSaveProfile = () => {
    if (!profile?.UserID) return;

    const nextFullName = fullName.trim();
    const nextMobileNumber = mobileNumber.trim();
    const currentFullName = profile.fullName.trim();
    const currentMobileNumber = (profile.mobileNumber || "").trim();

    if (
      nextFullName === currentFullName &&
      nextMobileNumber === currentMobileNumber
    ) {
      return;
    }

    updateProfile({
      fullName: nextFullName,
      mobileNumber: nextMobileNumber,
    });
  };

  return {
    t,
    i18n,
    profile,
    loading,
    updating,
    error,
    fullName,
    mobileNumber,
    setFullName,
    setMobileNumber,
    avatarInitials,
    showSkeleton,
    updateProfile,
    handleSaveProfile,
  };
};
